/**
 * Pear motion system — ported from JitsLab `web-v2/src/ui/motion.ts`.
 *
 * Two easing curves cover almost all of the tactile feel:
 *  - snap: fast, no overshoot — press-down, drags, anything immediate
 *  - pop:  short overshoot — selection confirms, entrances, the moment
 *          something "lands"
 *
 * The Velvet Room spec (`design/tokens.css`) is hex/spacing/type only and stays
 * untouched; these curves are component-level and live in app.css.
 */
import { useEffect, useState } from 'react';

export const EASE_SNAP = 'cubic-bezier(0.16, 1, 0.3, 1)';
export const EASE_POP = 'cubic-bezier(0.34, 1.56, 0.64, 1)';

export const DURATION = {
  fast: 180,
  normal: 260,
  slow: 320,
  slowest: 420,
} as const;

/**
 * Reduced motion — per component, NOT a global kill switch. Opacity fades can
 * stay while transform pops drop; each component decides.
 */
export function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);

    const handler = (e: MediaQueryListEvent) => setReduced(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  return reduced;
}

/** Motion-appropriate durations/easings, already respecting reduced motion. */
export function useMotion() {
  const reduced = usePrefersReducedMotion();
  return {
    reduced,
    duration: reduced ? 0 : DURATION.normal,
    ease: reduced ? 'linear' : EASE_SNAP,
    durationPop: reduced ? 0 : DURATION.normal,
    easePop: reduced ? 'linear' : EASE_POP,
  };
}
